import { MonthPicker } from '@/components/layout/MonthPicker';
import { PageHeader } from '@/components/layout/PageHeader';
import { EntityLink, NavAction } from '@/components/nav/EntityLink';
import { Donut } from '@/components/reports/Donut';
import { RateNotice } from '@/components/reports/RateNotice';
import { RevenueCell } from '@/components/reports/RevenueCell';
import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Meter } from '@/components/ui/meter';
import { Stat, StatRow } from '@/components/ui/stat';
import { Table, TableWrap, Td, TdNum, Th } from '@/components/ui/table';
import type { CapitalizationProject, CapitalizationReport } from '@/lib/api';
import { hours, percent, yen, yenExact } from '@/lib/format';
import { toProject, toTimeLogs, useMonthParam } from '@/lib/nav';
import { useApi } from '@/lib/useApi';

/**
 * 予算の 3 セル (BUDGET / USED / USAGE)。
 *
 * 「消化額」は月次モードなら当月、通期モードなら全期間からこの月までの累計。
 * 同じ列に期間の違う数字が並ぶので、予算の下に 通期 / 月次 のバッジを出す。
 * 既存の HOURS / COST / GROSS は常に当月のままで、意味は変えていない。
 *
 * 予算が無い行は 3 セルとも「—」。消化額だけ出ても読み手には意味がない。
 */
function BudgetCells({ project }: { project: CapitalizationProject }) {
  if (project.costBudget === null) {
    return (
      <>
        <TdNum className="text-ink-subtle">—</TdNum>
        <TdNum className="text-ink-subtle">—</TdNum>
        <TdNum className="text-ink-subtle">—</TdNum>
      </>
    );
  }

  const ratio = project.costUsageRate === null ? 0 : Math.min(1, project.costUsageRate / 100);

  return (
    <>
      <TdNum>
        {/* バッジと受注額を横に並べると 143px では収まらず、バッジ自体が
            「通」「期」に縦割れしていた。縦に積む。 */}
        <div className="flex flex-col items-end gap-1">
          <span>{yenExact(project.costBudget)}</span>
          <Badge variant="outline">{project.budgetMode === 'total' ? '通期' : '月次'}</Badge>
          {project.revenueBudget !== null ? (
            <span className="font-sans text-[11px] text-ink-subtle">
              {`受注 ${yenExact(project.revenueBudget)}`}
            </span>
          ) : null}
        </div>
      </TdNum>
      <TdNum>{project.budgetActualCost === null ? '—' : yenExact(project.budgetActualCost)}</TdNum>
      <TdNum>
        <div className="flex flex-col items-end gap-1.5">
          <span className={project.overBudget ? 'text-alert' : undefined}>
            {percent(project.costUsageRate)}
          </span>
          <Meter
            segments={[
              {
                ratio,
                className: project.overBudget ? 'bg-alert' : 'bg-brand-500',
                label: '消化',
              },
            ]}
          />
        </div>
      </TdNum>
    </>
  );
}

/** 収支・資産化レポート */
export function CapitalizationPage() {
  const [month, setMonth] = useMonthParam();
  const { data, error, loading } = useApi<CapitalizationReport>(
    `/api/reports/capitalization?month=${month}`,
  );

  const totals = data?.totals;
  const capRatio =
    totals && totals.actualHours > 0 ? totals.capitalizedHours / totals.actualHours : 0;

  return (
    <>
      <PageHeader
        title="収支・資産化レポート"
        subtitle={
          data
            ? `プロジェクト ${data.projects.length} 件${
                data.totals.overBudgetCount > 0
                  ? ` / 予算超過 ${data.totals.overBudgetCount} 件`
                  : ''
              }`
            : undefined
        }
        actions={
          <>
            <MonthPicker month={month} onChange={setMonth} />
            <a
              href={`/api/reports/capitalization.csv?month=${month}`}
              className={buttonVariants({ variant: 'primary' })}
            >
              CSV出力
            </a>
          </>
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        <StateBlock loading={loading} error={error} empty={data?.projects.length === 0}>
          {/* 上の要約も表と同じ扱いで、はみ出しを自分の中に閉じ込める。
              ドーナツ側が 380px 固定なので、残りの 1fr が数値タイルの min-content を
              下回ると、そのはみ出しがページ本体の横スクロールになって画面全体がずれる。 */}
          {totals ? (
            <div className="overflow-x-auto border border-line bg-surface-raised">
              <div className="grid min-w-[720px] grid-cols-[380px_1fr]">
                <div className="flex items-center gap-7 border-r border-line px-8 py-7">
                  <Donut ratio={capRatio} label={percent(totals.capRate)} />
                  <div className="flex flex-col gap-4">
                    <span className="text-[13px] font-medium">全社の工数構成</span>
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-xs text-ink-muted">
                        <span className="block h-2.5 w-2.5 bg-brand-500" />
                        資産化 Capitalized
                      </span>
                      <span className="pl-4.5 font-mono text-xl">
                        {hours(totals.capitalizedHours)}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="flex items-center gap-2 text-xs text-ink-muted">
                        <span className="block h-2.5 w-2.5 bg-line-bold" />
                        費用化 Expensed
                      </span>
                      <span className="pl-4.5 font-mono text-xl">
                        {hours(totals.expensedHours)}
                      </span>
                    </div>
                  </div>
                </div>

                <StatRow className="min-w-0 grid-cols-3 border-0">
                  <Stat
                    label="総工数コスト"
                    value={yen(totals.costAmount)}
                    hint={`実績 ${hours(totals.actualHours)}`}
                  />
                  <Stat
                    label="資産計上額"
                    value={yen(totals.capitalizedCost)}
                    hint={`資産化率 ${percent(totals.capRate)}`}
                    tone="brand"
                  />
                  {/* 全社の粗利も、単価が解決できないうちは数字を出さない。
                    出せない理由を hint に置き、直す先は表の上の RateNotice が示す。 */}
                  <Stat
                    label="粗利"
                    value={totals.grossProfit === null ? '—' : yen(totals.grossProfit)}
                    hint={
                      totals.revenueAmount === null
                        ? totals.revenueState === 'not_billable'
                          ? '請求可能な工数がありません'
                          : `売上単価が未設定 ${totals.unresolvedRateProjectCount} 件`
                        : `想定売上 ${yen(totals.revenueAmount)}`
                    }
                    tone={
                      totals.grossProfit !== null && totals.grossProfit < 0 ? 'alert' : 'default'
                    }
                  />
                </StatRow>
              </div>
            </div>
          ) : null}

          {totals ? <RateNotice count={totals.unresolvedRateProjectCount} /> : null}

          <div className="border border-line bg-surface-raised">
            <div className="flex items-baseline justify-between border-b border-line px-4 py-4">
              <span className="text-sm font-medium">プロジェクト別の資産化・費用化比率</span>
              <span className="font-mono text-[11px] tracking-wider text-ink-faint">
                CAPITALIZED / EXPENSED
              </span>
            </div>
            <TableWrap>
              {/* 幅は割合ではなく px で持つ。割合 (旧 MIX 26% / USAGE 14%) だと、
                  詰まるほどこの 2 列が他を圧迫し、1600px でも PROJECT が 97px しか
                  残らずプロジェクト名が 2〜3 行に割れていた。伸縮を受けるのは
                  PROJECT だけにする。
                  10 列とも別の事実を持つので落とさない。USED は月次モードでは
                  COST と同じ値だが、通期モードでは累計になり、そのときこそ
                  読みたい列。入りきらない幅では TableWrap が横スクロールする
                  (潰して折り返すより読める)。 */}
              <Table className="min-w-[1100px] md:min-w-[1100px]">
                <thead>
                  <tr>
                    <Th>PROJECT</Th>
                    <Th className="w-[88px]">MIX</Th>
                    <Th className="w-[88px] text-right">HOURS</Th>
                    <Th className="w-[112px] text-right">COST</Th>
                    <Th className="w-[112px] text-right">GROSS</Th>
                    <Th className="w-[80px] text-right">CAP. RATE</Th>
                    <Th className="w-[148px] text-right">BUDGET</Th>
                    <Th className="w-[112px] text-right">USED</Th>
                    <Th className="w-[96px] text-right">USAGE</Th>
                    <Th className="w-[112px]" />
                  </tr>
                </thead>
                <tbody>
                  {data?.projects.map((project) => {
                    const ratio =
                      project.actualHours > 0 ? project.capitalizedHours / project.actualHours : 0;
                    return (
                      <tr key={project.projectId}>
                        <Td>
                          <div className="flex flex-col gap-1">
                            {/* 名前からそのプロジェクトの設定へ。資産化対象の
                                切り替えや種別の付け替えはあちらでしかできない */}
                            <EntityLink
                              target={toProject({ id: project.projectId, name: project.name })}
                              className="font-medium"
                            >
                              {project.name}
                            </EntityLink>
                            <span className="text-[11px] text-ink-subtle">
                              {project.projectType ?? '種別未設定'}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <Meter
                            segments={[
                              { ratio, className: 'bg-brand-500', label: '資産化' },
                              { ratio: 1 - ratio, className: 'bg-line-bold', label: '費用化' },
                            ]}
                          />
                        </Td>
                        <TdNum>{hours(project.actualHours)}</TdNum>
                        <TdNum>{yenExact(project.costAmount)}</TdNum>
                        <RevenueCell
                          amount={project.grossProfit}
                          state={project.revenueState}
                          unratedHours={project.unratedBillableHours}
                        />
                        <TdNum>{percent(project.capRate)}</TdNum>
                        <BudgetCells project={project} />
                        <Td className="text-right">
                          {/* 数字の内訳を見に行く先。見ていた月をそのまま渡す */}
                          <NavAction target={toTimeLogs({ month, query: project.name })} />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>
          </div>
        </StateBlock>
      </div>
    </>
  );
}
